import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Imobiliario } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { getAll } from "../../../services/db";
import { extrairMensagemDeErro, getSituacaoIptu, unirImoveis } from "../../../services/api";
import toast from "react-hot-toast";
import { usaJoinImobiliario, usaSituacaoIptu } from "../../../constants/municipios";
import { DialogUnificarVinculosRioBranco } from "../DialogUnificarVinculosRioBranco";
import {
  getVinculosAtivosParaUnificacao,
  VinculoUnificacaoPayloadItem,
} from "../../../utils/unificarImobiliarioRioBranco";
import { ImobiliarioEdificacao } from "../../../interfaces";
import { formatNumeroUnidade } from "../../../utils/formatNumeroUnidade";

interface Props {
  municipio: string;
  openDialog: boolean;
  handleCloseDialog: () => void;
  fetchAndUpdateDashboardData: (id?: string, parameter?: string) => Promise<void>;
}

export function DialogJoin({ municipio, handleCloseDialog, openDialog, fetchAndUpdateDashboardData}: Props) {
  const enabled = usaJoinImobiliario(municipio);

  const { metadata, setMetadata } = useContext(DashboardContext);
  const [imoveis, setImoveis] = useState<Imobiliario[]>([]);
  const [selectedImovel, setSelectedImovel] = useState<Imobiliario | null>(null);
  const [imoveisIptu, setImoveisIptu] = useState<Record<number, string>>({});
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openMainDialog, setOpenMainDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openDialogVinculosRioBranco, setOpenDialogVinculosRioBranco] = useState(false);
  const situacoesIptuPermitidas = ["Não consta", "Imune", "Isento", "Cancelado", "Pago"];

  function handleCloseDialogs() {
    if (isProcessing) return;
    setOpenWarningDialog(false);
    setOpenMainDialog(false);
    handleCloseDialog();
  }

  async function fetchSituacaoIptu(idImobiliario: number) {
    if (!usaSituacaoIptu(municipio)) return null;

    try {
      const data = await getSituacaoIptu({
        municipio: municipio,
        id_imobiliario: [idImobiliario],
      });
      return data.length ? data[0].situacao_iptu : null;
    } catch (error) {
      toast.error("Erro ao buscar a situação do IPTU.");
      return null;
    }
  }

  useEffect(() => {
    async function verificarIptuPrincipal() {
      if (!enabled || !openDialog) return;

      {
        let situacao = null;
        if (usaSituacaoIptu(municipio)) {
          situacao = await fetchSituacaoIptu(Number(metadata.id_imobiliario));
        }
        if (situacao && !situacoesIptuPermitidas.includes(situacao)) {
          setOpenWarningDialog(true);
        } else {
          setOpenMainDialog(true);
        }
      }
    }
    verificarIptuPrincipal();
  }, [enabled, openDialog, municipio, metadata.id_imobiliario]);


  useEffect(() => {
    async function loadImoveis() {
      const idLote = metadata?.id_lote != null ? Number(metadata.id_lote) : null;
      const imoveisList = getAll("imobiliario")?.filter(
        (imovel: Imobiliario) =>
          (idLote == null || Number(imovel.id_lote) === idLote) &&
          imovel.id !== Number(metadata?.id_imobiliario) &&
          imovel.status !== 2 &&
          String(imovel.status) !== "2",
      ) as Imobiliario[];
  
      setImoveis(imoveisList);

      if (!usaSituacaoIptu(municipio)) {
        setImoveisIptu({});
        return;
      }

      const iptuStatus: Record<number, string> = {};
      for (const imovel of imoveisList) {
        const situacao = await fetchSituacaoIptu(imovel.id);
        if (situacao) {
          iptuStatus[imovel.id] = situacao;
        }
      }
      setImoveisIptu(iptuStatus);
    }
  
    if (enabled && openDialog) {
      loadImoveis();
    }
  }, [enabled, metadata, openDialog, municipio]);

  if (!enabled) {
    return null;
  }

  function handleSelectImovel(imovel: Imobiliario) {
    const situacaoImovel = imoveisIptu[imovel.id] || null;
    
    if (!situacoesIptuPermitidas.includes(situacaoImovel) && situacaoImovel !== null) {
      return;
    }
  
    setSelectedImovel((prev) => (prev?.id === imovel.id ? null : imovel));
  }
  

  function getVinculosAtivosImovel(idImobiliario: number) {
    const vinculos = (getAll("imobiliario_edificacao") || []) as ImobiliarioEdificacao[];
    return getVinculosAtivosParaUnificacao(vinculos, idImobiliario);
  }

  async function executarUnificacao(
    imovel: Imobiliario,
    vinculosEdificacao?: VinculoUnificacaoPayloadItem[],
  ) {
    try {
      setIsProcessing(true);
      const payloadExtra =
        municipio === "riobranco" && vinculosEdificacao?.length
          ? { vinculos_edificacao: vinculosEdificacao }
          : undefined;

      const resultado = await unirImoveis(metadata.id_imobiliario, Number(imovel.id), payloadExtra);

      if (resultado === "success") {
        setSelectedImovel(null);
        setOpenDialogVinculosRioBranco(false);
        handleCloseDialogs();

        await fetchAndUpdateDashboardData(
          String(imovel.id),
          "JoinImobiliario",
        );

        setMetadata((current) => ({ ...current }));
        toast.success("União realizada com sucesso!");
      } else {
        toast.error("Falha na união.");
      }
    } catch (error) {
      console.error("Erro ao realizar a união:", error);
      toast.error(extrairMensagemDeErro(error) ?? "Ocorreu um erro ao tentar unir os imóveis.");
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleJoin() {
    if (!selectedImovel) return;

    if (usaSituacaoIptu(municipio)) {
      const situacaoSelecionado =
        imoveisIptu[selectedImovel.id] ?? (await fetchSituacaoIptu(selectedImovel.id));
      if (situacaoSelecionado && !situacoesIptuPermitidas.includes(situacaoSelecionado)) {
        toast.error("A unidade selecionada possui débito em aberto e não pode ser unificada.");
        return;
      }
    }

    if (municipio === "riobranco") {
      const vinculos = getVinculosAtivosImovel(Number(selectedImovel.id));
      if (vinculos.length > 0) {
        setOpenDialogVinculosRioBranco(true);
        return;
      }
    }

    await executarUnificacao(selectedImovel);
  }

  async function handleConfirmarVinculosUnificacao(payload: VinculoUnificacaoPayloadItem[]) {
    if (!selectedImovel) return;
    await executarUnificacao(selectedImovel, payload);
  }

  return (
    <>
      <Dialog
        open={openWarningDialog}
        disableEscapeKeyDown={isProcessing}
        onClose={(_, reason) => {
          if (isProcessing) return;
          handleCloseDialogs();
        }}
      >
        <DialogContent>
          <Typography variant="h5">Aviso</Typography>
          <Typography variant="body1" style={{ textAlign: "center", marginTop: "10px" }}>
            O imóvel possui dívida de IPTU em aberto e não pode ser unificado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="success" variant="contained" disabled={isProcessing}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMainDialog}
        disableEscapeKeyDown={isProcessing}
        onClose={(_, reason) => {
          if (isProcessing) return;
          handleCloseDialogs();
        }}
      >
        <DialogContent>
          <h2 style={{ textAlign: "center" }}>Selecione uma unidade imobiliária para unir ao imóvel</h2>

          <List component="nav">
          {imoveis?.map((imovel: Imobiliario) => {
            const situacao = imoveisIptu[imovel.id] || null;
            const iptuPendencia = situacao !== null && !situacoesIptuPermitidas.includes(situacao);

            return (
              <ListItemButton
                key={imovel.id}
                sx={{
                  borderBottom: "1px solid #5f5f5f",
                  backgroundColor: selectedImovel?.id === imovel.id ? "#4b4b4b" : "inherit",
                  opacity: iptuPendencia ? 0.5 : 1,
                  cursor: iptuPendencia ? "not-allowed" : "pointer"
                }}
                onClick={() => !iptuPendencia && handleSelectImovel(imovel)}
              >
                <ListItemText
                  primary={imovel.inscricao_cartografica}
                  secondary={
                    iptuPendencia
                      ? `IPTU em aberto. Não pode ser unificado. Número da unidade imobiliária: ${formatNumeroUnidade(municipio, imovel.nr_unidade)} | Número de Cadastro: ${
                          imovel.numero_cadastro || "Não informado"
                        }`
                      : `Número da unidade imobiliária: ${formatNumeroUnidade(municipio, imovel.nr_unidade)} | Número de Cadastro: ${
                          imovel.numero_cadastro || "Não informado"
                        }`
                  }
                />
              </ListItemButton>
            );
          })}
          </List>
        </DialogContent>

        <DialogActions
          style={{
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Button
            color="success"
            onClick={handleJoin}
            variant="contained"
            disabled={!selectedImovel || isProcessing || openDialogVinculosRioBranco}
            startIcon={isProcessing ? <CircularProgress color="inherit" size={20} /> : undefined}
          >
            {isProcessing ? "Realizando unificação..." : "Unificar imóveis"}
          </Button>
        </DialogActions>
      </Dialog>

      {municipio === "riobranco" && selectedImovel && metadata.id_imobiliario && (
        <DialogUnificarVinculosRioBranco
          openDialog={openDialogVinculosRioBranco}
          idImobiliarioCorporadora={metadata.id_imobiliario}
          imovelUnificado={selectedImovel}
          idLote={metadata.id_lote}
          onClose={() => setOpenDialogVinculosRioBranco(false)}
          onConfirm={handleConfirmarVinculosUnificacao}
          processing={isProcessing}
        />
      )}
    </>
  );
}
